import React from 'react'
import {
  IonButton,
  IonButtons,
  IonCard,
  IonCardContent,
  IonCol,
  IonGrid,
  IonIcon,
  IonItem,
  IonItemOption,
  IonItemOptions,
  IonItemSliding,
  IonLabel,
  IonList,
  IonRow,
} from '@ionic/react'
import NumberPad from '../../../components/numberPad/NumberPad.component'
import SingleSelect from '../../../components/singleSelect/SingleSelect.component'
import TextArea from '../../../components/textArea/TextArea.component'
import { CcHpiConstants } from '../store/ccHpi.constants'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { getSituationSelector } from '../selectors'
import { IOtherComplaint, ISituationState } from '../store/ccHpi.types'
import { setOtherComplaints } from '../store/ccHpi.actions'
import { IOption } from '../../../common/common.types'
import { getCcHpiOptionsSelector } from '../../options/selectors'
import { ICcHpiOptionsState } from '../../options/store/options.types'
import { trash, addCircleOutline, trashOutline } from 'ionicons/icons'
import { otherComplaintInit } from '../store/ccHpi.reducers'
import { getDatapointsConfigSelector } from '../../configurations/selectors'
import { IDataPointsConfig, IServiceConfig } from '../../configurations/store/configurations.types'
import { getServiceConfigSelector } from '../../configurations/selectors/serviceConfig.selector'

const OtherComplaintsContent: React.FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { otherComplaints }: ISituationState = useSelector(getSituationSelector)
  const { ocDurationUnitsOptions }: ICcHpiOptionsState = useSelector(getCcHpiOptionsSelector)
  const { DP_COMPDURATION }: IDataPointsConfig = useSelector(getDatapointsConfigSelector)
  const { PR_COMPDURATION }: IServiceConfig = useSelector(getServiceConfigSelector)

  const addOtherComplaint = () => {
    const oComplaints = [...otherComplaints, otherComplaintInit()]
    dispatch(setOtherComplaints(oComplaints))
  }

  const deleteOtherComplaint = (otherComplaint: IOtherComplaint) => {
    dispatch(setOtherComplaints(otherComplaints.filter((oc) => oc.key !== otherComplaint.key)))
  }

  const otherComplaintOnChange = (e: CustomEvent, otherComplaint: IOtherComplaint) => {
    const updatedOtherComplaints = otherComplaints.map((oc) => {
      if (oc.key === otherComplaint.key) return { ...oc, otherComplaint: e.detail.value }
      return oc
    })
    dispatch(setOtherComplaints(updatedOtherComplaints))
  }

  const setDurationOnChange = (numbers: string[], otherComplaint: IOtherComplaint) => {
    const updatedOtherComplaints = otherComplaints.map((oc) => {
      if (oc.key === otherComplaint.key) return { ...oc, ocDuration: numbers.join('') }
      return oc
    })
    dispatch(setOtherComplaints(updatedOtherComplaints))
  }

  const setDurationUnitOnChange = (option: IOption, otherComplaint: IOtherComplaint) => {
    const updatedOtherComplaints = otherComplaints.map((oc) => {
      if (oc.key === otherComplaint.key) return { ...oc, ocDurationUnit: option }
      return oc
    })
    dispatch(setOtherComplaints(updatedOtherComplaints))
  }

  const otherComplaintContent = (otherComplaint: IOtherComplaint, index: number) => (
    <>
      <IonRow>
        <IonCol>
          {t('ccHpi.otherComplaint')} {index + 1}
        </IonCol>
        <IonButtons>
          <IonButton data-testid='deleteOtherComplaint' onClick={() => deleteOtherComplaint(otherComplaint)}>
            <IonIcon icon={trashOutline} />
          </IonButton>
        </IonButtons>
      </IonRow>

      <IonRow>
        <IonCol className='picklist'>
          <TextArea
            dataTestID='OtherComplaint'
            rows={3}
            cols={20}
            maxlength={255}
            value={otherComplaint.otherComplaint}
            validationId={CcHpiConstants.SET_OTHER_COMPLAINT}
            onChangeAction={(e) => {
              otherComplaintOnChange(e, otherComplaint)
            }}
          />
        </IonCol>
      </IonRow>

      <hr className='field-divider' />

      {(DP_COMPDURATION === 1 ||
        (DP_COMPDURATION === 2 && PR_COMPDURATION === 1) ||
        otherComplaint.ocDuration ||
        otherComplaint.ocDurationUnit) && (
        <IonRow>
          <IonCol className='picklist'>
            <NumberPad
              elementId={`OCDuration${index}`}
              dataTestId={`OCDuration${index}`}
              values={otherComplaint.ocDuration ? otherComplaint.ocDuration.split('') : []}
              label={t('ccHpi.duration')}
              onNumPadClick={(numbers) => setDurationOnChange(numbers, otherComplaint)}
            />
          </IonCol>
          <IonCol>
            <SingleSelect
              dataTestID='OCDurationUnit'
              options={ocDurationUnitsOptions}
              selectedOption={otherComplaint.ocDurationUnit}
              listHeader={t('ccHpi.durationUnit')}
              label={t('ccHpi.durationUnit')}
              action={(option) => setDurationUnitOnChange(option, otherComplaint)}
            />
          </IonCol>
        </IonRow>
      )}
    </>
  )

  return (
    <IonList>
      {otherComplaints &&
        otherComplaints.map((otherComplaint: IOtherComplaint, index) => (
          <IonItemSliding key={otherComplaint.key}>
            <IonCard className='ion-no-margin ion-margin-vertical' data-testid='OtherComplaint'>
              <IonCardContent>
                <IonGrid className='ion-no-padding'>{otherComplaintContent(otherComplaint, index)}</IonGrid>
              </IonCardContent>
            </IonCard>
            <IonItemOptions side='end' className='delete-swipe' data-testid='Swipe'>
              <IonItemOption
                color='danger'
                onDrag={() => deleteOtherComplaint(otherComplaint)}
                onClick={() => deleteOtherComplaint(otherComplaint)}>
                <IonIcon slot='icon-only' icon={trash} />
              </IonItemOption>
            </IonItemOptions>
          </IonItemSliding>
        ))}
      <IonRow className='ion-float-right'>
        <IonCol>
          <IonItem lines='none'>
            <IonButton
              className='transform-none multi-card-add-button'
              fill='clear'
              color='primary'
              onClick={addOtherComplaint}>
              <IonIcon icon={addCircleOutline} />
              <IonLabel>{t('ccHpi.addOtherComplaint')}</IonLabel>
            </IonButton>
          </IonItem>
        </IonCol>
      </IonRow>
    </IonList>
  )
}

export default OtherComplaintsContent
